import type { AssetListItem } from '@cartwhl/common/api/getAssets';
import type { NavigateFunction } from 'react-router';

import { getSceneMotionEditorPath } from './assetLibraryActions';
import {
  getAssetLibraryGroupKind,
  type AssetLibraryBatchGroupAssetType,
} from './assetLibraryGroupDetail';
import { parseJobAssetTypeFromShareParam } from './assetLibraryShareLink';
import { useStore } from './assetLibraryStore';
import type { AssetLibraryDisplayItem } from './utils';

type AssetLibraryStoreInstance = ReturnType<typeof useStore>;

function openJobPreviewFromShareLink(
  store: AssetLibraryStoreInstance,
  input: {
    jobId: string;
    groupID: string;
    assetTypeParam?: string | null;
    fallbackAssetType: AssetListItem['assetType'];
    isBookmarked: boolean;
  },
) {
  const assetType = (parseJobAssetTypeFromShareParam(input.assetTypeParam) ??
    input.fallbackAssetType) as AssetLibraryBatchGroupAssetType;

  store.openJobPreview({
    jobId: input.jobId,
    groupID: input.groupID,
    assetType,
    groupKind: getAssetLibraryGroupKind({
      assetType,
      groupID: input.groupID,
    }),
    isBookmarked: input.isBookmarked,
    showMainLibraryPreviewActions: true,
  });
}

export function applyAssetLibraryShareLinkAssets(
  store: AssetLibraryStoreInstance,
  navigate: NavigateFunction,
  assets: AssetListItem[],
  options: {
    previewId?: string | null;
    groupId?: string | null;
    assetTypeParam?: string | null;
  },
): boolean {
  if (assets.length === 0) {
    return false;
  }

  if (assets.length > 1) {
    store.openGroupDetail({
      ...assets[0],
      groupItemCount: assets.length,
    });

    if (options.previewId) {
      const previewAsset =
        assets.find((item) => item.assetID === options.previewId) ??
        assets[0];
      const groupID = options.groupId ?? previewAsset.groupID;
      if (groupID) {
        openJobPreviewFromShareLink(store, {
          jobId: options.previewId,
          groupID,
          assetTypeParam: options.assetTypeParam,
          fallbackAssetType: previewAsset.assetType,
          isBookmarked: Boolean(previewAsset.isBookmarked),
        });
      }
    }

    return true;
  }

  const asset = assets[0];
  const displayAsset: AssetLibraryDisplayItem = { ...asset };

  if (asset.assetType === 'SCENE') {
    navigate(getSceneMotionEditorPath(asset.assetID));
    return true;
  }

  if (asset.assetType === 'MEDIA_IMAGE' || asset.assetType === 'MEDIA_VIDEO') {
    store.openMediaPreviewFromAsset(displayAsset, {
      showMainLibraryPreviewActions: true,
      showOpenWithActions: true,
    });
    return true;
  }

  if (asset.assetType === 'MOGEN_JOB' || asset.assetType === 'MASCOT_JOB') {
    const jobId = options.previewId ?? asset.assetID;
    const groupID = options.groupId ?? asset.groupID;
    if (!groupID) {
      return false;
    }

    openJobPreviewFromShareLink(store, {
      jobId,
      groupID,
      assetTypeParam: options.assetTypeParam,
      fallbackAssetType: asset.assetType,
      isBookmarked: Boolean(asset.isBookmarked),
    });
    return true;
  }

  return false;
}
